<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="app-wrapper h-full min-h-0 w-full flex-grow-0"
    :class="{ 'app-rtl--wrapper': isRTLView }"
    :dir="isRTLView ? 'rtl' : 'ltr'"
  >
    <unpaid-invoice-banner v-if="isUnpaidInvoiceBannerVisible" />

    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
    <add-account-modal
      :show="showAddAccountModal"
      :has-accounts="hasAccounts"
    />
    <woot-snackbar-box />
    <network-notification />
  </div>
  <loading-state v-else />
</template>

<script>
import { mapGetters } from 'vuex';
import router from '../dashboard/routes';
import AddAccountModal from '../dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import UnpaidInvoiceBanner from './components/payments/UnpaidInvoiceBanner.vue';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import vueActionCable from './helper/actionCable';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import {
  bootstrapProductFruitsForUser,
  destroyProductFruitsCoordinator,
  updateProductFruitsLanguage,
} from '../productfruits/bootstrap';
import {
  SESSION_STORAGE_KEYS,
  sessionStorage,
} from 'dashboard/helper/sessionStorage';

import ManagementAPI from './api/management';

const PHOENIX_CONFIG_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

export default {
  name: 'App',

  components: {
    AddAccountModal,
    LoadingState,
    NetworkNotification,
    WootSnackbarBox,
    UnpaidInvoiceBanner,
  },

  mixins: [rtlMixin],

  data() {
    return {
      showAddAccountModal: false,
      latestChatwootVersion: null,
      reconnectService: null,
      phoenixImproveConfigInterval: null,
      phoenixSuggestionConfigInterval: null,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      currentAccountId: 'getCurrentAccountId',
      currentUserLanguage: 'management/getUserLanguage',
      isDark: 'theme/isDark',
      colorTheme: 'theme/colorTheme',
      currentChat: 'getSelectedChat',
      isPhoenixAccount: 'accounts/isPhoenix',
      paymentStatus: 'payments/getPaymentStatus',
      paymentsUIFlags: 'payments/getPaymentsUIFlags',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },

    isUnpaidInvoiceBannerVisible() {
      return (
        this.currentAccountId &&
        !this.paymentsUIFlags.isUnpaidInvoiceBannerDismissed &&
        !!this.paymentStatus?.hasOpenInvoices &&
        typeof this.paymentStatus?.timeUntilDue?.days === 'number' &&
        this.paymentStatus.timeUntilDue.days < 7
      );
    },
  },

  watch: {
    currentUser: {
      handler(user) {
        if (!this.hasAccounts) {
          this.showAddAccountModal = true;
        }

        if (user) {
          bootstrapProductFruitsForUser(user);
        }
      },
      immediate: false,
    },
    currentAccountId() {
      if (this.currentAccountId) {
        this.initializeAccount();
        this.getIsUnpaidInvoiceBannerDismissed();
        this.fetchPaymentStatus();
        this.fetchAINests();
        this.fetchAIApiKey();
        this.fetchKeystoneFlows();
        this.fetchPhoenixImproveConfig();
        this.fetchPhoenixSuggestionConfig();
      }
    },
    currentChat() {
      // Refresh suggestion config when switching conversations/inboxes
      this.fetchPhoenixSuggestionConfig();
    },

    '$root.$i18n.locale': {
      handler(newValue) {
        this.$store.dispatch('deepl/getTranslationLanguages', {
          language: newValue,
        });
        // Update Product Fruits language when locale changes
        if (this.currentUser) {
          updateProductFruitsLanguage(newValue, this.currentUser);
        }
      },
      immediate: false,
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    this.setLocale(window.chatwootConfig.selectedLocale);
  },
  beforeDestroy() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    if (this.phoenixImproveConfigInterval) {
      clearInterval(this.phoenixImproveConfigInterval);
    }
    if (this.phoenixSuggestionConfigInterval) {
      clearInterval(this.phoenixSuggestionConfigInterval);
    }
    destroyProductFruitsCoordinator();
  },
  methods: {
    initializeColorTheme() {
      this.$store.dispatch('theme/getColorTheme');
      setColorTheme(this.isDark);
    },

    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = (e) => {
        if (this.colorTheme === 'auto') {
          this.$store.commit('theme/updateIsDark');
          setColorTheme(e.matches);
        }
      };
    },

    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },

    async initializeAccount() {
      await this.$store.dispatch('accounts/setAccount', this.currentAccountId);

      const {
        locale: accountLocale,
        latest_chatwoot_version: latestChatwootVersion,
      } = this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken } = this.currentUser || {};

      await this.$store.dispatch('management/getUserLanguage', {
        currentUserId: this.currentUser.id,
      });

      await this.$store.dispatch('management/getUserProfileDetails', {
        userId: this.currentUser.id,
      });

      const locale = this.currentUserLanguage || accountLocale;

      if (this.$root.$i18n.locale === locale) {
        this.$store.dispatch('deepl/getTranslationLanguages', {
          language: locale,
        });
      }

      this.setLocale(locale);
      this.updateRTLDirectionView(locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(pubsubToken);
      this.reconnectService = new ReconnectService(this.$store, router);

      verifyServiceWorkerExistence((registration) =>
        registration.pushManager.getSubscription().then((subscription) => {
          if (subscription) {
            registerSubscription(
              () => {},
              this.$t('PROFILE_SETTINGS.FORM.API.BROWSER_PUSH_ENABLED'),
              this.$t('PROFILE_SETTINGS.FORM.API.BROWSER_PUSH_NOT_SUPPORTED')
            );
          }
        })
      );
    },

    async fetchAINests() {
      await this.$store.dispatch('chatbot/getAINests', {
        accountId: this.currentAccountId,
      });
    },

    async fetchAIApiKey() {
      await ManagementAPI.getAIApiKey(this.currentAccountId);
    },

    async fetchKeystoneFlows() {
      await this.$store.dispatch('management/getKeystoneFlows', {
        accountId: this.currentAccountId,
      });
    },

    async fetchPhoenixImproveConfig() {
      if (!this.currentAccountId) {
        return;
      }

      // Capture account ID to detect if it changes during fetch
      const accountIdAtStart = this.currentAccountId;

      await this.$store.dispatch(
        'inboxes/fetchPhoenixImproveConfig',
        accountIdAtStart
      );

      // Abort if account changed during fetch (avoid setting up interval for old account)
      if (this.currentAccountId !== accountIdAtStart) {
        return;
      }

      // Clear any existing interval and set up periodic refresh (every 30 minutes)
      if (this.phoenixImproveConfigInterval) {
        clearInterval(this.phoenixImproveConfigInterval);
      }

      this.phoenixImproveConfigInterval = setInterval(() => {
        if (this.currentAccountId) {
          this.$store.dispatch(
            'inboxes/fetchPhoenixImproveConfig',
            this.currentAccountId
          );
        }
      }, PHOENIX_CONFIG_REFRESH_INTERVAL);
    },

    async fetchPhoenixSuggestionConfig() {
      const inboxId = this.currentChat?.inbox_id;

      if (!inboxId) {
        return;
      }

      const currentInbox = this.$store.getters['inboxes/getInbox'](inboxId);
      const hasIdentifier =
        !!currentInbox?.additional_attributes?.apiInboxIdentifier;

      // Only fetch for Phoenix inboxes with identifier
      if (!this.isPhoenixAccount || !hasIdentifier) {
        return;
      }

      // Capture inbox ID and account ID to detect if it changes during fetch
      const inboxIdAtStart = inboxId;
      const accountIdAtStart = this.currentAccountId;

      await this.$store.dispatch('inboxes/fetchPhoenixSuggestionConfig', {
        inboxId: inboxIdAtStart,
        accountId: accountIdAtStart,
      });

      // Abort if inbox changed during fetch (avoid setting up interval for old inbox)
      if (this.currentChat?.inbox_id !== inboxIdAtStart) {
        return;
      }

      // Clear any existing interval and set up periodic refresh (every 30 minutes)
      if (this.phoenixSuggestionConfigInterval) {
        clearInterval(this.phoenixSuggestionConfigInterval);
      }

      this.phoenixSuggestionConfigInterval = setInterval(() => {
        const currentInboxId = this.currentChat?.inbox_id;
        if (currentInboxId && this.currentAccountId) {
          this.$store.dispatch('inboxes/fetchPhoenixSuggestionConfig', {
            inboxId: currentInboxId,
            accountId: this.currentAccountId,
          });
        }
      }, PHOENIX_CONFIG_REFRESH_INTERVAL);
    },

    getIsUnpaidInvoiceBannerDismissed() {
      this.$store.dispatch(
        'payments/setIsUnpaidInvoiceBannerDismissed',
        sessionStorage.get(
          SESSION_STORAGE_KEYS.IS_UNPAID_INVOICE_BANNER_DISMISSED
        ) ?? false
      );
    },

    async fetchPaymentStatus() {
      if (!this.currentAccountId) {
        return;
      }

      await this.$store.dispatch(
        'payments/getPaymentStatus',
        this.currentAccountId
      );
    },
  },
};
</script>

<style lang="scss">
@import './assets/scss/app';
</style>
